// src/components/PatientAppointments.js
import React, { useEffect, useState, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import api from "../utils/api";
import "./PatientAppointments.css";

const PatientAppointments = () => {
  const { patientUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchCriteria, setSearchCriteria] = useState({
    patientName: "",
    doctorName: "",
    appointmentDate: "",
  });

  useEffect(() => {
    const fetchAppointments = async () => {
      if (!patientUser || patientUser.role !== "patient") {
        setError("You are not authorized to view this page.");
        setIsLoading(false);
        return;
      }
      try {
        console.log("Fetching appointments for patient:", patientUser.id);
        const response = await api.get(`/appointments/patient/${patientUser.id}`, {
          headers: { Authorization: `Bearer ${patientUser.accessToken}` },
        });
        console.log("Fetched appointments:", response.data);
        setAppointments(response.data || []);
        setFilteredAppointments(response.data || []);
      } catch (err) {
        console.error("Failed to fetch appointments:", err.response?.data || err.message);
        setError("Failed to load appointments: " + (err.response?.data?.message || err.message));
      } finally {
        setIsLoading(false);
      }
    };
    fetchAppointments();
  }, [patientUser]);

  const handleViewMore = useCallback((appointmentId) => {
    console.log("Attempting to view appointment ID:", appointmentId);
    try {
      console.log("Current appointments list:", appointments);
      const appointment = appointments.find(app => app._id === appointmentId);
      console.log("Found appointment:", appointment);
      if (!appointment) {
        console.error("Appointment not found in patient’s list:", appointmentId);
        setError("This appointment is not in your list.");
        return;
      }
      console.log("Appointment patientId:", appointment.patientId, "Logged-in patientId:", patientUser.id);
      if (!appointment.patientId || appointment.patientId !== patientUser.id) {
        console.error("Unauthorized attempt to view appointment:", appointmentId);
        setError("You are not authorized to view this appointment.");
        return;
      }
      navigate(`/patient/appointment-detail/${appointmentId}`);
    } catch (err) {
      console.error("Error in handleViewMore:", err);
      setError("An unexpected error occurred while navigating.");
    }
  }, [appointments, patientUser, navigate]);

  const handleDownloadPrescription = async (appointmentId) => {
    try {
      console.log(`Fetching prescription for appointment ${appointmentId}`);
      const prescriptionRes = await api.get(`/prescriptions/appointment/${appointmentId}`, {
        headers: { Authorization: `Bearer ${patientUser.accessToken}` },
      });
      const prescription = prescriptionRes.data;
      if (!prescription || !prescription.pdfPath) {
        setError("No prescription file available to download for this appointment.");
        setTimeout(() => setError(null), 3000);
        return;
      }

      const response = await api.get(`/appointments/prescriptions/${prescription._id}/download-direct`, {
        headers: { Authorization: `Bearer ${patientUser.accessToken}` },
        responseType: 'blob', // Handle binary data
      });

      const filename = `Prescription-${new Date(prescription.createdAt).toLocaleDateString().replace(/\//g, '-')}.pdf`;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading prescription:", err.response?.data || err.message);
      const errorMessage = err.response?.data?.message || err.message;
      const errorDetails = err.response?.data?.details || 'No additional details available';
      setError(`Failed to download prescription: ${errorMessage} - ${errorDetails}`);
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchCriteria((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    const filterAppointments = () => {
      let filtered = [...appointments];

      if (searchCriteria.patientName) {
        filtered = filtered.filter((appointment) =>
          (appointment.name || "You")
            .toLowerCase()
            .includes(searchCriteria.patientName.toLowerCase())
        );
      }

      if (searchCriteria.doctorName) {
        filtered = filtered.filter((appointment) =>
          (appointment.doctorId?.name || "Unknown")
            .toLowerCase()
            .includes(searchCriteria.doctorName.toLowerCase())
        );
      }

      if (searchCriteria.appointmentDate) {
        const searchDate = new Date(searchCriteria.appointmentDate).toDateString();
        filtered = filtered.filter((appointment) =>
          new Date(appointment.date).toDateString() === searchDate
        );
      }

      setFilteredAppointments(filtered);
    };

    filterAppointments();
  }, [searchCriteria, appointments]);

  if (isLoading) return <div>Loading...</div>;
  if (error) return (
    <div className="pap-container">
      <main className="pap-main">
        <h1 className="pap-title">Error</h1>
        <p className="pap-text">{error}</p>
        <button className="pap-button" onClick={() => navigate(`/patient-dashboard/${patientUser?.id || ''}`)}>
          Back to Dashboard
        </button>
      </main>
    </div>
  );

  return (
    <div className="pap-container">
      <main className="pap-main">
        <h1 className="pap-title">Your Appointments</h1>
        <div className="pap-search-container">
          <input
            type="text"
            name="patientName"
            placeholder="Search by Patient Name"
            value={searchCriteria.patientName}
            onChange={handleSearchChange}
            className="pap-search-input"
          />
          <input
            type="text"
            name="doctorName"
            placeholder="Search by Doctor Name"
            value={searchCriteria.doctorName}
            onChange={handleSearchChange}
            className="pap-search-input"
          />
          <input
            type="date"
            name="appointmentDate"
            value={searchCriteria.appointmentDate}
            onChange={handleSearchChange}
            className="pap-search-input"
          />
        </div>
        {filteredAppointments.length === 0 ? (
          <p className="pap-text">No appointments found.</p>
        ) : (
          <div className="pap-appointment-list">
            {filteredAppointments.map((appointment) => (
              <div key={appointment._id} className="pap-appointment-card">
                <div className="card-header">
                  <span className="appointment-id">ID: {appointment._id}</span>
                  <span className={`status-badge ${appointment.status?.toLowerCase()}`}>
                    {appointment.status || "N/A"}
                  </span>
                </div>
                <div className="card-content">
                  <p><strong>Patient:</strong> {appointment.name || "You"}</p>
                  <p><strong>Age:</strong> {appointment.age || "N/A"}</p>
                  <p><strong>Gender:</strong> {appointment.gender || "N/A"}</p>
                  <p><strong>Doctor:</strong> Dr. {appointment.doctorId?.name || "Unknown"}</p>
                  <p><strong>Date & Time:</strong> {new Date(appointment.date).toLocaleString()}</p>
                  <p><strong>Payment:</strong>
                    <span className={`payment-status ${appointment.paymentStatus?.toLowerCase()}`}>
                      {appointment.paymentStatus || "Pending"}
                    </span>
                  </p>
                  <p><strong>Prescription:</strong>
                    {appointment.prescriptionStatus === "Completed" ? (
                      <button
                        className="download-btn"
                        onClick={() => handleDownloadPrescription(appointment._id)}
                        aria-label="Download prescription PDF"
                      >
                        Download PDF
                      </button>
                    ) : (
                      appointment.prescriptionStatus || "Not Yet Provided"
                    )}
                  </p>
                  <p><strong>Completed:</strong>
                    {appointment.completedAt ? new Date(appointment.completedAt).toLocaleString() : "N/A"}
                  </p>
                </div>
                <button
                  className="view-more-btn"
                  onClick={() => handleViewMore(appointment._id)}
                >
                  View More Details
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default PatientAppointments;